import { useSelector } from 'react-redux';
import { useMyForm } from '../../../../../../../react-commons/hooks/useMyForm';
import { QueryBaseComponent } from '../../../../../../../react-commons/query/QueryBaseComponent';
import { ResourceType } from '../../../../../resource/enumerations/ResourceType';
import {
    defaultVerificationMetricResource,
    useVerificationMetricActions,
    VerificationMetricMutationResourceSchema,
    VerificationMetricPropertyValidation,
} from '../../../../../resource/resources/artifacts/verification_metric/verificationMetricResource';
import { Form_DisplayComponentBaseView } from '../../../../reusable-components/form-components/Form_DisplayComponentBaseView';
import { SingleParentedOverviewComponent } from '../../../../reusable-components/resource-overviews/SingleParentedOverviewComponent';
import { selectNoScopeValidationScope, selectReadOnly } from '../../../../util/useSelectorFunctions';
import { VerificationMetricContent } from './VerificationMetricContent';

const VerificationMetricOverviewContent = ({
    id,
    displayedInOverview,
}: {
    id: string;
    displayedInOverview: boolean;
}) => {
    const readOnly = useSelector(selectReadOnly);

    const verificationMetricActions = useVerificationMetricActions();
    const verificationMetricQuery = verificationMetricActions.useGetResource(id);
    const updateVerificationMetric = verificationMetricActions.useUpdateResource();

    const { form, formNames, onSubmit } = useMyForm({
        schema: VerificationMetricMutationResourceSchema,
        defaultValues: defaultVerificationMetricResource,
        submitHandler: async (data) => {
            await updateVerificationMetric.mutateAsync({
                resource: data,
                id: data.id,
            });
        },
        reactiveValues: verificationMetricQuery.data,
    });

    return (
        <QueryBaseComponent name="VerificationMetric" query={verificationMetricQuery}>
            <Form_DisplayComponentBaseView onSubmit={onSubmit} form={form} canSubmitDefaultValue={false}>
                <VerificationMetricContent
                    displayedInOverview={displayedInOverview}
                    form={form}
                    formNames={formNames}
                    readOnly={readOnly}
                    id={id}
                />
            </Form_DisplayComponentBaseView>
        </QueryBaseComponent>
    );
};

export const VerificationMetricOverviewComponent = ({ parentId }: { parentId: string }) => {
    const validationScope = useSelector(selectNoScopeValidationScope);

    const verificationMetricActions = useVerificationMetricActions();

    const parametersQuery = verificationMetricActions.useGetAllResources(parentId);

    const resourceType = ResourceType.VERIFICATION_METRIC;

    return (
        <QueryBaseComponent name="VerificationMetrics" query={parametersQuery}>
            <SingleParentedOverviewComponent
                label={'Verification Metrics'}
                resourceQuery={parametersQuery}
                content={(id, displayedInOverview) => (
                    <VerificationMetricOverviewContent id={id} displayedInOverview={displayedInOverview} />
                )}
                propertyValidationInformation={{
                    namePropertyIdentifier: VerificationMetricPropertyValidation.NAME,
                    resourceType,
                }}
                validationScope={validationScope}
                parentId={parentId}
            />
        </QueryBaseComponent>
    );
};
